import React, { useState, useEffect } from "react";
import { Layout, Form, Tree, Card, theme } from "antd";
import type { TreeProps } from "antd";
import { PlusOutlined, FolderOutlined, FileTextOutlined } from "@ant-design/icons";
import { HeaderApp, NavigationBar } from "../../shared";
import HeaderPage from "../../shared/components/HeaderPage";
import EmptyData from "../../shared/components/EmptyData";
import Loading from "../../shared/components/Loading";
import DrawerDetails from "../../shared/components/DrawerDetails";
import openNotification from "../../../utils/openNotification";
import type { ChartAccountData } from "../types";
import { useFetchChartAccounts } from "../hooks/useFetchChartAccounts";
import { useCreateChartAccount } from "../hooks/useCreateChartAccount";
import { useEditChartAccount } from "../hooks/useEditChartAccount";
import { useDeleteChartAccount } from "../hooks/useDeleteChartAccount";
import ChartAccountForm from "../components/ChartAccountForm";
import ChartAccountDetails from "../components/ChartAccountDetails";

// Tree building helper
const buildAccountTree = (accounts: ChartAccountData[]): any[] => {
  const accountMap: { [key: number]: any } = {};
  const roots: any[] = [];

  // Initialize map
  accounts.forEach((account) => {
    accountMap[account.id_chart_account!] = {
      title: (
        <span style={{ fontSize: "15px", fontWeight: 600 }}>
          {account.grouping_code} - {account.account_name}
        </span>
      ),
      key: String(account.id_chart_account),
      icon: account.id_parent_chart_account ? <FileTextOutlined /> : <FolderOutlined />,
      data: account,
      children: [],
    };
  });

  // Build tree
  accounts.forEach((account) => {
    const node = accountMap[account.id_chart_account!];
    if (account.id_parent_chart_account) {
      const parent = accountMap[account.id_parent_chart_account];
      if (parent) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    } else {
      roots.push(node);
    }
  });

  // Clean empty children
  const cleanTree = (nodes: any[]) => {
    nodes.forEach((node) => {
      if (node.children.length === 0) {
        delete node.children;
      } else {
        cleanTree(node.children);
      }
    });
  };

  cleanTree(roots);
  return roots;
};

const ChartAccounts: React.FC = () => {
  const { token } = theme.useToken();
  const [form] = Form.useForm<ChartAccountData>();

  // Fetch accounts
  const { data: accounts, isLoading } = useFetchChartAccounts();

  // Mutations
  const { mutate: createAccount, isPending: isCreating } = useCreateChartAccount();
  const { mutate: editAccount, isPending: isEditing } = useEditChartAccount();
  const { mutate: deleteAccount, isPending: isDeleting } = useDeleteChartAccount();

  // Drawers and selections
  const [selectedAccount, setSelectedAccount] = useState<ChartAccountData | null>(null);
  const [createDrawerVisible, setCreateDrawerVisible] = useState(false);
  const [editDrawerVisible, setEditDrawerVisible] = useState(false);
  const [parentAccountForSub, setParentAccountForSub] = useState<ChartAccountData | null>(null);

  // Synchronize form on drawer change
  useEffect(() => {
    if (editDrawerVisible && selectedAccount) {
      form.setFieldsValue(selectedAccount);
    } else if (createDrawerVisible) {
      if (parentAccountForSub) {
        form.setFieldsValue({
          id_parent_chart_account: parentAccountForSub.id_chart_account,
          level: parentAccountForSub.level + 1,
          type_account: parentAccountForSub.type_account,
          account_name: "",
          grouping_code: undefined,
        });
      } else {
        form.setFieldsValue({
          id_parent_chart_account: undefined,
          level: 1,
          type_account: "Deudora",
          account_name: "",
          grouping_code: undefined,
        });
      }
    } else {
      form.resetFields();
    }
  }, [createDrawerVisible, editDrawerVisible, selectedAccount, parentAccountForSub, form]);

  const handleCreateFinish = (values: ChartAccountData) => {
    // If it's a parent account, remove id_parent_chart_account
    const payload = { ...values };
    if (!parentAccountForSub) {
      delete payload.id_parent_chart_account;
    } else {
      payload.id_parent_chart_account = parentAccountForSub.id_chart_account;
    }

    createAccount(payload, {
      onSuccess: () => {
        openNotification("success", "Cuenta creada exitosamente");
        setCreateDrawerVisible(false);
        setParentAccountForSub(null);
      },
      onError: (error) => {
        openNotification("error", "Error", error.message);
      },
    });
  };

  const handleEditFinish = (values: ChartAccountData) => {
    const payload = {
      ...values,
      id_chart_account: selectedAccount?.id_chart_account,
      id_parent_chart_account: selectedAccount?.id_parent_chart_account,
    };

    editAccount(payload, {
      onSuccess: () => {
        openNotification("success", "Cuenta actualizada exitosamente");
        setEditDrawerVisible(false);
        setSelectedAccount(null);
      },
      onError: (error) => {
        openNotification("error", "Error", error.message);
      },
    });
  };

  const handleDelete = () => {
    if (!selectedAccount?.id_chart_account) return;

    deleteAccount(selectedAccount.id_chart_account, {
      onSuccess: () => {
        openNotification("success", "Cuenta eliminada exitosamente");
        setSelectedAccount(null);
      },
      onError: (error) => {
        openNotification("error", "Error", error.message);
      },
    });
  };

  if (isLoading) {
    return <Loading />;
  }

  const treeNodes = accounts ? buildAccountTree(accounts) : [];

  const onSelect: TreeProps["onSelect"] = (selectedKeys, info) => {
    if (info.selected && info.node) {
      const nodeData = (info.node as any).data as ChartAccountData;
      setSelectedAccount(nodeData);
    }
  };

  return (
    <>
      <HeaderApp sectionName="Catálogo | Plan de Cuentas" showBack />
      <Layout.Content
        style={{ padding: "16px", width: "100%", backgroundColor: token.colorBgLayout }}
        className="max-width-container"
      >
        <HeaderPage
          title="Plan de Cuentas"
          subtitle="Gestiona el catálogo de cuentas de tu empresa"
          actions={[
            {
              label: "Agregar cuenta principal",
              icon: <PlusOutlined />,
              type: "primary",
              onClick: () => {
                setParentAccountForSub(null);
                setCreateDrawerVisible(true);
              },
            },
          ]}
        />

        <main style={{ marginTop: "16px" }}>
          {treeNodes.length === 0 ? (
            <EmptyData description="No hay cuentas registradas" />
          ) : (
            <Card style={{ borderRadius: token.borderRadiusLG, border: `1px solid ${token.colorBorderSecondary}` }}>
              <Tree
                showIcon
                showLine={{ showLeafIcon: false }}
                defaultExpandAll
                onSelect={onSelect}
                treeData={treeNodes}
                style={{ fontSize: "16px", padding: "8px 0" }}
              />
            </Card>
          )}
        </main>

        {/* Details Drawer */}
        <DrawerDetails
          title="Detalles de la Cuenta"
          onClose={() => setSelectedAccount(null)}
          open={!!selectedAccount && !editDrawerVisible && !createDrawerVisible}
          contentBody={
            selectedAccount ? (
              <ChartAccountDetails
                account={selectedAccount}
                onEdit={() => setEditDrawerVisible(true)}
                onDelete={handleDelete}
                isDeleting={isDeleting}
                onAddSubAccount={() => {
                  setParentAccountForSub(selectedAccount);
                  setCreateDrawerVisible(true);
                }}
              />
            ) : null
          }
        />

        {/* Create Drawer */}
        <DrawerDetails
          title={parentAccountForSub ? `Agregar Sub-cuenta bajo ${parentAccountForSub.account_name}` : "Agregar Cuenta Principal"}
          onClose={() => {
            setCreateDrawerVisible(false);
            setParentAccountForSub(null);
          }}
          open={createDrawerVisible}
          contentBody={
            <div style={{ maxWidth: 640, margin: "0 auto" }}>
              <ChartAccountForm
                form={form}
                onFinish={handleCreateFinish}
                isPending={isCreating}
                buttonText="Crear Cuenta"
                formId="create-chart-account-form"
              />
            </div>
          }
        />

        {/* Edit Drawer */}
        <DrawerDetails
          title="Editar Cuenta"
          onClose={() => setEditDrawerVisible(false)}
          open={editDrawerVisible}
          contentBody={
            <div style={{ maxWidth: 640, margin: "0 auto" }}>
              <ChartAccountForm
                form={form}
                onFinish={handleEditFinish}
                isPending={isEditing}
                buttonText="Actualizar Cuenta"
                formId="edit-chart-account-form"
              />
            </div>
          }
        />

        <NavigationBar activeKey="/catalogos" />
      </Layout.Content>
    </>
  );
};

export default ChartAccounts;
